import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAuth } from '../context/AuthContext';
import {
  AgentDashboardSummary,
  getAgentDashboardSummary,
} from '../api/dashboard';
import { extractApiError } from '../api/legalServices';

interface StatCardProps {
  icon: string;
  value: string;
  label: string;
  change: string;
  changeColor?: string;
  changeIcon?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  value,
  label,
  change,
  changeColor = 'var(--green)',
  changeIcon = 'trending-up',
  onClick,
}) => (
  <div
    className="card min-w-0 p-4 sm:p-[22px]"
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') onClick();
          }
        : undefined
    }
    style={{
      borderRadius: 16,
      transition: 'box-shadow .2s',
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    <div
      className="rounded-xl flex items-center justify-center mb-3 sm:mb-3.5 w-10 h-10 sm:w-11 sm:h-11"
      style={{
        background: 'var(--brand-light)',
        color: 'var(--brand)',
      }}
    >
      <Icon name={icon} size={20} color="var(--brand)" />
    </div>
    <div
      className="font-extrabold text-ink leading-none truncate text-[22px] sm:text-[28px]"
      style={{ letterSpacing: '-0.5px' }}
      title={value}
    >
      {value}
    </div>
    <div className="text-g500" style={{ fontSize: 13, marginTop: 5 }}>
      {label}
    </div>
    <div
      className="flex items-start gap-1 font-semibold"
      style={{ fontSize: 12, marginTop: 8, color: changeColor }}
    >
      <Icon name={changeIcon} size={14} color={changeColor} className="flex-shrink-0 mt-px" />
      <span className="min-w-0">{change}</span>
    </div>
  </div>
);

const initials = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const AgentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] || 'Merchant';

  const [data, setData] = useState<AgentDashboardSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAgentDashboardSummary();
      setData(res);
    } catch (err) {
      setError(extractApiError(err, 'Failed to load dashboard'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="py-20 text-center text-g500">
        <Icon name="layout-dashboard" size={28} color="var(--g400)" /> Loading
        dashboard...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="card p-6 max-w-[520px]">
        <div
          className="flex items-center gap-2 mb-2"
          style={{ color: 'var(--red)' }}
        >
          <Icon name="x-circle" size={18} color="var(--red)" />
          <strong>Couldn't load dashboard</strong>
        </div>
        <div className="text-sm text-g600 mb-4">
          {error || 'No data returned.'}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  const { stats, newInquiries, recentInquiries, topListings } = data;

  return (
    <div>
      {/* Welcome banner */}
      <div
        className="flex justify-between items-center gap-4 flex-wrap mb-5 sm:mb-6 p-5 sm:px-8 sm:py-[26px]"
        style={{
          borderRadius: 20,
          background:
            'linear-gradient(135deg, rgba(225,1,116,0.04) 0%, rgba(225,1,116,0.015) 50%, rgba(37,99,235,0.024) 100%)',
          border: '1px solid rgba(225,1,116,0.07)',
        }}
      >
        <div>
          <h2
            className="font-extrabold text-ink"
            style={{ fontSize: 20, letterSpacing: '-0.3px' }}
          >
            Welcome back, {firstName}!
          </h2>
          <p className="text-g500" style={{ fontSize: 14, marginTop: 5 }}>
            {newInquiries > 0 ? (
              <>
                You have{' '}
                <Link
                  to="/agent/inquiries"
                  className="font-semibold"
                  style={{
                    color: 'var(--brand)',
                    textDecoration: 'underline',
                    textDecorationColor: 'rgba(225,1,116,0.25)',
                    textUnderlineOffset: 2,
                  }}
                >
                  {newInquiries} new {newInquiries === 1 ? 'inquiry' : 'inquiries'}
                </Link>{' '}
                waiting for your review.
              </>
            ) : (
              <>You're all caught up — no new inquiries right now.</>
            )}
          </p>
        </div>
        <button
          className="btn btn-primary w-full justify-center sm:w-auto"
          onClick={() => navigate('/agent/listings/new')}
        >
          <Icon name="plus" size={18} color="#fff" /> Add New Listing
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-7">
        <StatCard
          icon="building-2"
          value={String(stats.activeListings.value)}
          label="Active Listings"
          change={stats.activeListings.change}
          onClick={() => navigate('/agent/listings')}
        />
        <StatCard
          icon="message-square"
          value={String(stats.totalInquiries.value)}
          label="Total Inquiries"
          change={stats.totalInquiries.change}
          changeColor={
            stats.totalInquiries.up ? 'var(--green)' : 'var(--red)'
          }
          changeIcon={
            stats.totalInquiries.up ? 'trending-up' : 'trending-down'
          }
          onClick={() => navigate('/agent/inquiries')}
        />
        <StatCard
          icon="eye"
          value={stats.listingViews.valueLabel ?? String(stats.listingViews.value)}
          label="Listing Views"
          change={stats.listingViews.change}
          changeColor="var(--g500)"
          changeIcon="eye"
        />
        <StatCard
          icon="star"
          value={stats.plan.name}
          label="Current Plan"
          change={stats.plan.statusLabel ?? (stats.plan.active ? 'Active' : 'Inactive')}
          changeColor="var(--brand)"
          changeIcon="check-circle"
          onClick={() => navigate('/agent/subscription')}
        />
      </div>

      {/* Two-column grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {/* Recent Inquiries */}
        <div className="card min-w-0 p-4 sm:p-6" style={{ borderRadius: 16 }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-ink" style={{ fontSize: 15 }}>
              Recent Inquiries
            </h3>
            <button
              onClick={() => navigate('/agent/inquiries')}
              className="font-semibold flex items-center gap-1 hover:underline"
              style={{ fontSize: 13, color: 'var(--brand)' }}
            >
              View all <Icon name="chevron-right" size={14} color="var(--brand)" />
            </button>
          </div>
          <div>
            {recentInquiries.length === 0 && (
              <div className="py-8 text-center text-g500" style={{ fontSize: 13 }}>
                No inquiries yet. They'll appear here as soon as someone
                reaches out about one of your listings.
              </div>
            )}
            {recentInquiries.map((iq, i) => (
              <div
                key={iq.id}
                onClick={() => navigate('/agent/inquiries')}
                className="flex items-center gap-3 cursor-pointer"
                style={{
                  padding: '12px 0',
                  borderTop: i > 0 ? '1px solid var(--g100)' : 'none',
                }}
              >
                <div
                  className="rounded-full flex items-center justify-center font-bold flex-shrink-0"
                  style={{
                    width: 36,
                    height: 36,
                    background: 'var(--brand-bg)',
                    color: 'var(--brand)',
                    fontSize: 12,
                  }}
                >
                  {initials(iq.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="font-semibold text-ink"
                    style={{ fontSize: 13.5 }}
                  >
                    {iq.name}
                    {iq.isNew && (
                      <span
                        className="ml-2 font-bold"
                        style={{
                          fontSize: 10,
                          padding: '2px 7px',
                          borderRadius: 100,
                          background: 'var(--brand-bg)',
                          color: 'var(--brand)',
                          letterSpacing: '0.3px',
                        }}
                      >
                        NEW
                      </span>
                    )}
                  </div>
                  <div
                    className="text-g500 truncate"
                    style={{ fontSize: 12, marginTop: 1 }}
                  >
                    Interested in {iq.property}
                  </div>
                </div>
                <span
                  className="text-g400 whitespace-nowrap flex-shrink-0"
                  style={{ fontSize: 11.5 }}
                >
                  {iq.time}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Listings */}
        <div className="card min-w-0 p-4 sm:p-6" style={{ borderRadius: 16 }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-ink" style={{ fontSize: 15 }}>
              Top Performing Listings
            </h3>
            <button
              onClick={() => navigate('/agent/listings')}
              className="font-semibold flex items-center gap-1 hover:underline"
              style={{ fontSize: 13, color: 'var(--brand)' }}
            >
              View all <Icon name="chevron-right" size={14} color="var(--brand)" />
            </button>
          </div>
          <div>
            {topListings.length === 0 && (
              <div className="py-8 text-center text-g500" style={{ fontSize: 13 }}>
                No listings yet.{' '}
                <Link
                  to="/agent/listings/new"
                  className="font-semibold"
                  style={{ color: 'var(--brand)' }}
                >
                  Add your first listing
                </Link>{' '}
                to start tracking performance.
              </div>
            )}
            {topListings.map((l, i) => (
              <div
                key={l.id}
                onClick={() => navigate('/agent/listings')}
                className="flex items-center gap-3 cursor-pointer"
                style={{
                  padding: '12px 0',
                  borderTop: i > 0 ? '1px solid var(--g100)' : 'none',
                }}
              >
                <div
                  className="rounded-[10px] flex items-center justify-center flex-shrink-0"
                  style={{
                    width: 40,
                    height: 40,
                    background: 'var(--g100)',
                  }}
                >
                  <Icon name="building-2" size={18} color="var(--g500)" />
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="font-semibold text-ink truncate"
                    style={{ fontSize: 13.5 }}
                  >
                    {l.addr}
                  </div>
                  <div
                    className="flex items-center gap-x-3 gap-y-0.5 flex-wrap text-g500"
                    style={{ fontSize: 11.5, marginTop: 2 }}
                  >
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <Icon name="eye" size={12} color="var(--g400)" />
                      {l.views} views
                    </span>
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <Icon name="message-square" size={12} color="var(--g400)" />
                      {l.inquiries} inquiries
                    </span>
                  </div>
                </div>
                <span
                  className="flex items-center gap-1.5 font-semibold capitalize flex-shrink-0"
                  style={{
                    fontSize: 12,
                    color:
                      l.status === 'active'
                        ? 'var(--green)'
                        : 'var(--yellow)',
                  }}
                >
                  <span
                    className="rounded-full"
                    style={{
                      width: 7,
                      height: 7,
                      background:
                        l.status === 'active'
                          ? 'var(--green)'
                          : 'var(--yellow)',
                    }}
                  />
                  {l.status === 'active' ? 'active' : 'paused'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
